import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "gt-banner is-orange"
}
const _hoisted_2 = {
  key: 1,
  class: "gt-banner is-orange"
}

import { ref, onMounted, computed } from 'vue';
import {
  BaseDialog,
  BaseTextContent,
  BaseButton,
  BaseIcon,
  XrkMessageBox,
  XrkMessage
} from 'xrk-components';
import { WarningFilled } from '@element-plus/icons-vue';
import {
  invoiceDetail,
  auditInvoice,
  saveInvoice,
  redInvoice
  // openReject
} from '@V/invoice/src/data-source/apis';
import type {
  AuditInvoiceParams,
  GtInvoiceDetailData
  // OpenReject
} from '@V/invoice/src/data-source/types';
import type { DetailType, GtButtonSource } from '@V/invoice/src/types';
import InvoiceDetailDialogInfo from './invoice-detail-dialog-info.vue';
import InvoiceDetailDialogDemand from './invoice-detail-dialog-demand.vue';
import InvoiceBlurredUpload from './invoice-blurred-upload.vue';

/* ==================== Props ==================== */

/** 弹窗场景：1=服务商个体 3=服务商平台 11=代办供应商 21=OSS-个体 23=OSS-平台 */
type InvoiceDetailScene = 1 | 3 | 11 | 21 | 23;

const detailTypeNum: DetailType = 1;

/* ==================== Mode Computed ==================== */


export default /*#__PURE__*/_defineComponent({
  __name: 'invoice-detail-gt-dialog',
  props: {
    id: {},
    scene: { default: 1 },
    buttonSource: {},
    onSuccess: {},
    remove: {}
  },
  emits: ["success"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

/* ==================== Title Map ==================== */

const TITLE_MAP: Partial<Record<GtButtonSource, string>> = {
  1: '上传蓝票',
  2: '补充蓝票',
  3: '修改上传',
  4: '上传红票',
  5: '修改上传红票',
  6: '审核蓝票',
  7: '审核红票',
  8: '申请冲红',
  9: '重新申请冲红',
  10: '继续冲红',
  11: '发票详情',
  15: '补充红票'
};

const dialogTitle = computed(() => TITLE_MAP[props.buttonSource] || '发票详情');

/* ==================== 老流程兼容字段 ==================== */

/**
 * detailTypeNum 为老流程兼容字段（仅 InvoiceDetailDialogInfo 使用），
 * 新流程子组件 invoice-blurred-upload 直接接收 GtButtonSource（buttonSource），
 * 此处统一硬编码为 1。
 */
const isDetailMode = computed(() => props.buttonSource === 11);
const isAuditBlueMode = computed(() => props.buttonSource === 6);
const isAuditRedMode = computed(() => props.buttonSource === 7);
const isAuditMode = computed(
  () => isAuditBlueMode.value || isAuditRedMode.value
);
const isAuditRed = computed(() => isAuditRedMode.value);
const isRedApplyMode = computed(() => [8, 9, 10].includes(props.buttonSource));

/** 审核操作区（驳回/通过）——仅服务商个体场景 */
const showAudit = computed(
  () => [1, 3].includes(props.scene) && isAuditMode.value
);

/** 拒绝开票按钮是否可见 —— 只有销方且发票处于待确认状态，展示该操作 */
// 该操作在列表中展示
// const showOpenReject = computed(() => {
//   if ([1, 3, 23].includes(props.scene)) return false;
//   const bizType = formData.value.info.invoiceBusinessType;
//   // 限定业务类型 4/5/7/8/9/10 才有拒绝
//   if (![4, 5, 7, 8, 9, 10].includes(bizType)) return false;
//   // 详情模式且非审核态
//   if (!isDetailMode.value || isAuditMode.value) return false;
//   const bs = formData.value.info.blueInvoiceStatus;
//   const rs = formData.value.info.redInvoiceStatus;
//   // 蓝票 -2/0/1/3/11/12/13 可拒绝，红票 8/4 可拒绝
//   return (
//     (bs !== undefined && [-2].includes(bs)) ||
//     (rs !== undefined && [8].includes(rs))
//   );
// });

/** 是否展示提交按钮（按 scene 限定可提交的 buttonSource 集合） */
const showSubmitBtn = computed(() => {
  const bs = props.buttonSource;
  if (props.scene === 3) {
    // 服务商平台：全集合
    return [1, 2, 3, 4, 5, 15, 8, 9, 10].includes(bs);
  }
  if ([11, 23].includes(props.scene)) {
    // 代办供应商：上传/修改/补充蓝红票，无审核无冲红申请
    return [1, 2, 3, 4, 5, 15].includes(bs);
  }
  // 服务商个体：全集合
  return [1, 2, 3, 4, 5, 15, 8, 9, 10].includes(bs);
});

/* ==================== Refs & Form Data ==================== */

const infoRef = ref(null);
const dataRef = ref(null);

const formData = ref<GtInvoiceDetailData>({
  blueRedInvoiceList: [],
  fileGroup: { blueFileList: [], redFileList: [] },
  info: {
    id: 0,
    supplierId: 0,
    supplierName: '',
    aspId: 0,
    aspName: '',
    itemName: '',
    itemId: 0,
    invoiceStatus: 0,
    openAt: '',
    invoiceAmount: 0,
    createAt: '',
    invoiceTypeName: '',
    invoiceType: 0,
    invoiceTitle: '',
    invoicePhone: '',
    invoiceIdentifierNo: '',
    invoiceOpenBank: '',
    invoiceBankNo: '',
    invoiceEnterpriseAddress: '',
    isSupplement1: 0,
    isSupplement2: 0,
    receiptFullName: '',
    receiptPhone: '',
    receiptAddress: '',
    authAt: '',
    noPassReason: '',
    invoiceBusinessType: null,
    invoiceOrderNo: '',
    dkPeopleFinishSum: 0,
    dkPeopleTotalSum: 0,
    updateAt: '',
    platformServicesPrice: 0,
    personalScalePrice: 0,
    taskAt: '',
    invoiceMethod: 0,
    taxRate: 0,
    email: '',
    receiptAddressName: '',
    orderMixtureType: null,
    professionType: 0,
    theSeller: 0,
    theSellerName: '',
    demand: '',
    theBuyerName: '',
    blueInvoiceStatus: 0,
    redInvoiceStatus: 0,
    redAmount: 0,
    sumAmount: 0,
    itemDetailList: []
  } as GtInvoiceDetailData['info']
});

/** 审核提交参数（服务商个体审核蓝/红票） */
const auditParams = ref<AuditInvoiceParams>({
  invoiceApplyId: null,
  invoiceStatus: null,
  invoiceType: null,
  noPassReason: null
});

/** 申请冲红金额（元） */
const redAmount = ref(0);

const info = computed(() => formData.value.info);

/* ---------------- 驳回 banner ---------------- */
const rejectBanner = computed(() => {
  const { blueInvoiceStatus, redInvoiceStatus, noPassReason } = info.value;
  if (!noPassReason) return '';
  if (redInvoiceStatus === 7) return `购方驳回原因：${noPassReason}。`;
  if (redInvoiceStatus === 9) {
    return `冲红申请驳回原因：${noPassReason}。`;
  }
  if (blueInvoiceStatus === 3) return `购方驳回原因：${noPassReason}。`;
  if (blueInvoiceStatus === 13) return `拒绝开票原因：${noPassReason}。`;
  return '';
});

/* ==================== 审核逻辑（服务商个体） ==================== */

/** 审核-通过 */
const onAuditPass = async () => {
  auditParams.value.invoiceStatus = 1;
  auditParams.value.noPassReason = '';
  auditParams.value.invoiceType = isAuditRed.value ? 2 : 1;
  await submitAudit();
};

/** 审核-驳回 */
const onAuditReject = async () => {
  const data = await XrkMessageBox.prompt('请输入驳回原因', '提示', {
    cancelButtonText: '取消',
    confirmButtonText: '确认',
    inputType: 'textarea',
    inputErrorMessage: '请输入1-200个字符',
    inputValidator(v) {
      const _v = v.trim();
      return _v.length > 0 && _v.length <= 200;
    }
  });
  auditParams.value.invoiceStatus = 0;
  auditParams.value.noPassReason = data.value.trim();
  auditParams.value.invoiceType = isAuditRed.value ? 2 : 1;
  await submitAudit();
};

const submitAudit = async () => {
  await auditInvoice(auditParams.value);
  XrkMessage.success('操作成功');
  onSuccess();
};

/** 拒绝开票（销方） — bizType 4/5/8/9 需二次确认 */
// const onOpenReject = async () => {
//   const bizType = formData.value.info.invoiceBusinessType;
//   const needConfirm = [4, 5, 8, 9].includes(bizType);

//   if (needConfirm) {
//     const confirmed = await XrkMessageBox.confirm(
//       '拒绝后该订单将被释放，是否继续？',
//       '提示',
//       { cancelButtonText: '取消', confirmButtonText: '确认' }
//     );
//     if (!confirmed) return;
//   }

//   const data = await XrkMessageBox.prompt('请输入驳回原因', '提示', {
//     cancelButtonText: '取消',
//     confirmButtonText: '确认',
//     inputType: 'textarea',
//     inputErrorMessage: '请输入1-200个字符',
//     inputValidator(v) {
//       const _v = v.trim();
//       return _v.length > 0 && _v.length <= 200;
//     }
//   });
//   if (!data || !data.value) return;

//   await openReject({
//     invoiceApplyId: formData.value.info.id,
//     invoiceStatus: formData.value.info.invoiceStatus,
//     noPassReason: data.value.trim()
//   } as Parameters<OpenReject>[0]);
//   XrkMessage.success('操作成功');
//   emits('success');
//   onCancel();
// };

/* ==================== 提交逻辑 ==================== */

/** 提交 */
const onConfirm = async () => {
  const { buttonSource } = props;

  // ===== 上传蓝票 / 修改蓝票 / 补充蓝票 =====
  if ([1, 3, 2].includes(buttonSource)) {
    await dataRef.value?.validate();
    await submitSaveInvoice(1);
    return;
  }

  // ===== 上传红票 / 修改红票 / 补充红票 =====
  if ([4, 5, 15].includes(buttonSource)) {
    await dataRef.value?.validate();
    await submitSaveInvoice(2);
    return;
  }

  // ===== 申请冲红 / 重新申请冲红 / 继续冲红 =====
  if (isRedApplyMode.value) {
    await dataRef.value.validate();
    XrkMessageBox.prompt('请输入红冲原因', '提示', {
      cancelButtonText: '取消',
      confirmButtonText: '确认',
      inputType: 'textarea',
      inputErrorMessage: '请输入1-200个字符',
      inputValidator(v: string) {
        const _v = v.trim();
        return _v.length > 0 && _v.length <= 200;
      }
    }).then(async promptV => {
      // 冲红金额保持元，元→分 由数据源层 redInvoice 统一处理
      await redInvoice({
        invoiceApplyId: props.id,
        reason: promptV.value.trim(),
        redAmount: redAmount.value,
        buttonSource: props.buttonSource
      });
      XrkMessage.success('操作成功');
      onSuccess();
    });
  }
};

/** 提交 saveInvoice */
const submitSaveInvoice = async (invoiceType: 1 | 2) => {
  const invoiceApplyId = formData.value.info.id;

  // blueRedInvoiceList 元→分 由数据源层 saveInvoice 统一处理
  await saveInvoice({
    buttonSource: props.buttonSource,
    blueRedInvoiceList: formData.value.blueRedInvoiceList,
    fileGroup: formData.value.fileGroup,
    id: invoiceApplyId,
    invoiceApplyId,
    importType: 0,
    invoiceType,
    createAt: formData.value.info.createAt
      ? new Date(formData.value.info.createAt).getTime()
      : Date.now(),
    taskAt: formData.value.info.taskAt || ''
  });

  XrkMessage.success('操作成功');
  onSuccess();
};

/* ==================== Helpers ==================== */

const onSuccess = () => {
  emits('success');
  props.onSuccess?.();
  props.remove?.();
};

const onCancel = () => {
  props.remove?.();
};

/* ==================== Init ==================== */

onMounted(async () => {
  const { data } = await invoiceDetail({ id: props.id });
  const gtData = data as unknown as GtInvoiceDetailData;

  const { info: detailInfo, blueRedInvoiceList, fileGroup } = gtData;

  formData.value = {
    blueRedInvoiceList: blueRedInvoiceList || [],
    fileGroup: {
      blueFileList: fileGroup?.blueFileList || [],
      redFileList: fileGroup?.redFileList || []
    },
    info: { ...formData.value.info, ...detailInfo }
  };

  // 初始化审核数据
  const auditInvoiceType = [0, 1, 2, 3].includes(detailInfo.invoiceStatus)
    ? 1
    : 2;
  auditParams.value = {
    invoiceApplyId: props.id,
    invoiceStatus: [2, 6].includes(detailInfo.invoiceStatus)
      ? 1
      : [3, 7].includes(detailInfo.invoiceStatus)
      ? 0
      : null,
    invoiceType: auditInvoiceType as AuditInvoiceParams['invoiceType'],
    noPassReason: ''
  };
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BaseDialog), {
    width: "950px",
    class: "form-dialog form-dialog--label120 form-dialog-invoice-custom",
    title: dialogTitle.value,
    "model-value": true,
    "close-on-press-escape": true,
    "close-on-click-modal": false,
    showFooter: false
  }, {
    footer: _withCtx(() => [
      _createVNode(_unref(BaseButton), {
        type: "default",
        onClick: onCancel
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(isDetailMode.value ? '关闭' : '取消'), 1)
        ]),
        _: 1
      }),
      (showAudit.value)
        ? (_openBlock(), _createBlock(_unref(BaseButton), {
            key: 0,
            type: "warning",
            onClick: onAuditReject
          }, {
            default: _withCtx(() => [
              _createTextVNode(" 驳回 ")
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (showAudit.value)
        ? (_openBlock(), _createBlock(_unref(BaseButton), {
            key: 1,
            onClick: onAuditPass
          }, {
            default: _withCtx(() => [
              _createTextVNode("通过")
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (showSubmitBtn.value)
        ? (_openBlock(), _createBlock(_unref(BaseButton), {
            key: 2,
            onClick: onConfirm
          }, {
            default: _withCtx(() => [
              _createTextVNode("提交")
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(BaseTextContent), null, {
        default: _withCtx(() => [
          (info.value.delayInvoiceReason)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(BaseIcon), { size: "16" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(WarningFilled))
                  ]),
                  _: 1
                }),
                _createElementVNode("span", null, "延迟开票原因：" + _toDisplayString(info.value.delayInvoiceReason), 1)
              ]))
            : _createCommentVNode("", true),
          (rejectBanner.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_unref(BaseIcon), { size: "16" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(WarningFilled))
                  ]),
                  _: 1
                }),
                _createElementVNode("span", null, _toDisplayString(rejectBanner.value), 1)
              ]))
            : _createCommentVNode("", true),
          _createVNode(InvoiceDetailDialogInfo, {
            ref_key: "infoRef",
            ref: infoRef,
            detailType: detailTypeNum,
            "invoice-business-type": formData.value.info.invoiceBusinessType,
            modelValue: formData.value.info,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.info) = $event))
          }, null, 8, ["invoice-business-type", "modelValue"]),
          _createVNode(InvoiceDetailDialogDemand, {
            modelValue: formData.value.info,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.info) = $event))
          }, null, 8, ["modelValue"]),
          _createVNode(InvoiceBlurredUpload, {
            ref_key: "dataRef",
            ref: dataRef,
            buttonSource: _ctx.buttonSource,
            id: _ctx.id,
            formData: formData.value,
            "onUpdate:formData": _cache[2] || (_cache[2] = ($event: any) => ((formData).value = $event)),
            redAmount: redAmount.value,
            "onUpdate:redAmount": _cache[3] || (_cache[3] = ($event: any) => ((redAmount).value = $event))
          }, null, 8, ["buttonSource", "id", "formData", "redAmount"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})